import React from 'react';
import { Pagination, PaginationContent, PaginationItem } from '#/components/ui/pagination';
import { Button } from '#/components/ui/button';
import Icon from './Icon';

type TablePaginationProps = {
  page: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
};

function getPageNumbers(current: number, total: number, delta = 1): (number | string)[] {
  const pages: (number | string)[] = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  pages.push(1);

  if (left > 2) pages.push('...');

  for (let i = left; i <= right; i++) {
    pages.push(i);
  }

  if (right < total - 1) pages.push('...');

  if (total > 1) pages.push(total);

  return pages;
}

export function TablePagination({ page, totalItems, itemsPerPage, onPageChange }: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pages = getPageNumbers(page, totalPages);

  return (
    <Pagination>
      <PaginationContent className="flex items-center justify-center gap-2">
        <PaginationItem>
          <Button disabled={page <= 1} onClick={() => onPageChange(page - 1)} size="icon" variant="outline">
            <Icon name="keyboard_arrow_left" />
          </Button>
        </PaginationItem>

        {pages.map((p, i) => (
          <PaginationItem key={i}>
            {p === '...' ? (
              <Button
                disabled
                size="icon"
                variant="outline"
                className="border-[var(--border-color)] text-[var(--gray-primary)]"
              >
                {p}
              </Button>
            ) : (
              <Button
                onClick={() => onPageChange(p as number)}
                variant="outline"
                size="icon"
                className={`hover:bg-accent hover:text-primary rounded-md text-sm ${page === p ? 'border-primary text-primary' : 'border-[var(--border-color)] text-[var(--gray-primary)]'}`}
              >
                {p}
              </Button>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <Button size="icon" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} variant="outline">
            <Icon name="keyboard_arrow_right" />
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
